import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { toast } from 'sonner'
import {
  createShift,
  getShifts,
  getShiftTemplates,
  getShiftById,
  updateShift,
  deleteShift,
  createShiftAssignment,
  bulkAssignShifts,
  getShiftAssignments,
  getMyShiftAssignments,
  getRecentCustomShiftSlots,
  getShiftAssignmentById,
  updateShiftAssignment,
  confirmShiftAssignment,
  acknowledgeShiftAlert,
  cancelShiftAssignment,
  deleteShiftAssignment,
  previewRotaUpload,
  commitRotaUpload,
} from '@/api/services/shifts.service'
import { getErrorMessage } from '@/lib/getErrorMessage'
import {
  EMPLOYEE_NOT_ELIGIBLE,
  getShiftEligibilityErrorMessage,
  summarizeIneligibleBulkErrors,
} from '@/features/shifts/utils/shiftEligibility'
import { notificationsQueryKeys } from '@/features/notifications/notificationQueryKeys'
import { attendanceKeys } from '@/features/attendance/hooks/useAttendance'
import { invalidateDashboards } from '@/features/dashboard/hooks/useDashboardStats'
import type {
  ShiftFilterQueryDto,
  ShiftAssignmentFilterQueryDto,
  CreateShiftDto,
  UpdateShiftDto,
  CreateShiftAssignmentDto,
  UpdateShiftAssignmentDto,
  BulkAssignShiftDto,
  CancelShiftAssignmentDto,
  BulkAssignShiftResult,
  RotaConflictPolicy,
} from '@/types/shift.types'

function summarizeBulkAssignErrors(
  errors: BulkAssignShiftResult['errors'],
  userNames?: Record<string, string>
): string {
  const reasons = [
    ...new Set(
      errors
        .filter((error) => error.code !== EMPLOYEE_NOT_ELIGIBLE)
        .map((error) => error.reason)
        .filter(Boolean)
    ),
  ]
  return [reasons.slice(0, 2).join(' '), summarizeIneligibleBulkErrors(errors, userNames)]
    .filter(Boolean)
    .join(' ')
}

function getBulkAssignToast(
  result: Pick<BulkAssignShiftResult, 'created' | 'skipped' | 'errors' | 'summary'>,
  userNames?: Record<string, string>
): { variant: 'success' | 'warning' | 'error'; message: string } {
  const created = Number(result.created ?? result.summary?.totalCreated ?? 0)
  const failed = Number(result.summary?.totalFailed ?? result.errors?.length ?? 0)
  const skipped = Number(result.skipped ?? result.summary?.totalSkipped ?? 0)
  const requested = Number(result.summary?.totalRequested ?? created + failed + skipped)
  const reasonSummary = summarizeBulkAssignErrors(result.errors ?? [], userNames)

  if (created > 0 && failed === 0) {
    return {
      variant: skipped > 0 ? 'warning' : 'success',
      message:
        skipped > 0
          ? `${created} of ${requested} shift assignments created, ${skipped} skipped.`
          : `${created} shift assignment${created === 1 ? '' : 's'} created.`,
    }
  }

  if (created > 0) {
    return {
      variant: 'warning',
      message: `${created} of ${requested} shift assignments created, ${failed} failed.${
        reasonSummary ? ` ${reasonSummary}` : ''
      }`,
    }
  }

  return {
    variant: 'error',
    message: `${failed || requested} of ${requested} shift assignments failed.${
      reasonSummary ? ` ${reasonSummary}` : ''
    }`,
  }
}

function extractBulkAssignErrorResult(error: unknown): BulkAssignShiftResult | null {
  const axiosError = error as AxiosError<{
    errors?: Array<{ userId?: string; date?: string; reason?: string; code?: string }>
    data?: Partial<BulkAssignShiftResult>
  }>
  const body = axiosError.response?.data
  if (!body || typeof body !== 'object') return null

  const nested = body.data
  if (nested && typeof nested === 'object') {
    return {
      created: Number(nested.created ?? 0),
      skipped: Number(nested.skipped ?? 0),
      errors: Array.isArray(nested.errors) ? nested.errors : [],
      summary:
        nested.summary && typeof nested.summary === 'object'
          ? {
              totalRequested: Number(nested.summary.totalRequested ?? 0),
              totalCreated: Number(nested.summary.totalCreated ?? nested.created ?? 0),
              totalFailed: Number(nested.summary.totalFailed ?? nested.errors?.length ?? 0),
              totalSkipped: Number(nested.summary.totalSkipped ?? nested.skipped ?? 0),
            }
          : undefined,
    }
  }

  if (Array.isArray(body.errors) && body.errors.length > 0) {
    const normalizedErrors = body.errors.map((entry) => ({
      userId: entry.userId ?? '',
      date: entry.date ?? '',
      reason: entry.reason ?? 'Validation failed',
      code: entry.code,
    }))
    return {
      created: 0,
      skipped: 0,
      errors: normalizedErrors,
      summary: {
        totalRequested: normalizedErrors.length,
        totalCreated: 0,
        totalFailed: normalizedErrors.length,
        totalSkipped: 0,
      },
    }
  }

  return null
}

export const shiftsQueryKeys = {
  all: (companyId: string | null) => ['shifts', companyId] as const,
  list: (companyId: string | null, params: ShiftFilterQueryDto | undefined) =>
    ['shifts', companyId, 'list', params] as const,
  templates: (companyId: string | null) => ['shifts', companyId, 'templates'] as const,
  detail: (companyId: string | null, id: string) => ['shifts', companyId, 'detail', id] as const,
  recentCustomSlots: (companyId: string | null) =>
    ['shifts', companyId, 'recent-custom-slots'] as const,
}

export const shiftAssignmentsQueryKeys = {
  all: (companyId: string | null) => ['shift-assignments', companyId] as const,
  list: (companyId: string | null, params: ShiftAssignmentFilterQueryDto | undefined) =>
    ['shift-assignments', companyId, 'list', params] as const,
  me: (companyId: string | null, params: ShiftAssignmentFilterQueryDto | undefined) =>
    ['shift-assignments', companyId, 'me', params] as const,
  detail: (companyId: string | null, id: string) =>
    ['shift-assignments', companyId, 'detail', id] as const,
}

/**
 * Every cache key any planning-grid view reads from. The cross-company hooks
 * (useShiftsAcrossCompanies.ts) have their own prefixes that the per-company
 * `shiftAssignmentsQueryKeys.all` doesn't reach — so a per-company invalidation
 * silently misses the ShiftManagementPage grid.
 */
const ASSIGNMENT_VIEW_PREFIXES = [
  ['shift-assignments'],
  ['shift-assignments-overview'],
  ['shift-assignments-overview-me'],
  ['shifts-roster'],
] as const

async function invalidateAllAssignmentViews(
  queryClient: ReturnType<typeof useQueryClient>
): Promise<void> {
  await Promise.all([
    ...ASSIGNMENT_VIEW_PREFIXES.map((queryKey) =>
      queryClient.invalidateQueries({ queryKey, refetchType: 'all' })
    ),
    // Scheduled / no-show / attendance % on both dashboards follow assignments.
    invalidateDashboards(queryClient),
  ])
}

async function invalidateRecentCustomSlotViews(
  queryClient: ReturnType<typeof useQueryClient>
): Promise<void> {
  await queryClient.invalidateQueries({
    predicate: (query) =>
      Array.isArray(query.queryKey) &&
      query.queryKey[0] === 'shifts' &&
      query.queryKey[2] === 'recent-custom-slots',
    refetchType: 'all',
  })
}

export function useShifts(
  companyId: string | null,
  params: ShiftFilterQueryDto | undefined,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: shiftsQueryKeys.list(companyId, params),
    queryFn: () => getShifts(companyId!, params),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: !!companyId && options?.enabled !== false,
  })
}

export function useShiftTemplates(companyId: string | null) {
  return useQuery({
    queryKey: shiftsQueryKeys.templates(companyId),
    queryFn: () => getShiftTemplates(companyId!),
    enabled: !!companyId,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

export function useShift(companyId: string | null, id: string | null) {
  return useQuery({
    queryKey: shiftsQueryKeys.detail(companyId, id ?? ''),
    queryFn: () => getShiftById(companyId!, id!),
    enabled: !!companyId && !!id,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

export function useCreateShift() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ companyId, payload }: { companyId: string; payload: CreateShiftDto }) =>
      createShift(companyId, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: shiftsQueryKeys.all(variables.companyId),
      })
      toast.success('Shift created')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useUpdateShift() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      companyId,
      id,
      payload,
    }: {
      companyId: string
      id: string
      payload: UpdateShiftDto
    }) => updateShift(companyId, id, payload),
    onSuccess: async (updated, variables) => {
      queryClient.invalidateQueries({
        queryKey: shiftsQueryKeys.all(variables.companyId),
      })
      queryClient.invalidateQueries({
        queryKey: shiftsQueryKeys.detail(variables.companyId, variables.id),
      })
      // A template edit re-syncs future assignments server-side, so refresh the
      // assignment views too and tell the user how many shifts were updated.
      const propagated = updated?.propagatedAssignmentsCount ?? 0
      if (propagated > 0) {
        await invalidateAllAssignmentViews(queryClient)
        toast.success(
          `Shift updated. ${propagated} future assignment${
            propagated === 1 ? '' : 's'
          } updated to match. Employees whose times changed were notified to re-confirm.`,
        )
      } else {
        toast.success('Shift updated')
      }
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useDeleteShift() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ companyId, id }: { companyId: string; id: string }) =>
      deleteShift(companyId, id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: shiftsQueryKeys.all(variables.companyId),
      })
      queryClient.invalidateQueries({
        queryKey: ['shifts-overview'],
        refetchType: 'all',
      })
      queryClient.invalidateQueries({
        queryKey: shiftAssignmentsQueryKeys.all(variables.companyId),
      })
      void invalidateDashboards(queryClient)
      toast.success('Shift deleted')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useShiftAssignments(
  companyId: string | null,
  params: ShiftAssignmentFilterQueryDto | undefined,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: shiftAssignmentsQueryKeys.list(companyId, params),
    queryFn: () => getShiftAssignments(companyId!, params),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: !!companyId && options?.enabled !== false,
  })
}

export function useMyShiftAssignments(
  companyId: string | null,
  params: ShiftAssignmentFilterQueryDto | undefined,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: shiftAssignmentsQueryKeys.me(companyId, params),
    queryFn: () => getMyShiftAssignments(companyId!, params),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: !!companyId && options?.enabled !== false,
  })
}

export function useShiftAssignment(companyId: string | null, id: string | null) {
  return useQuery({
    queryKey: shiftAssignmentsQueryKeys.detail(companyId, id ?? ''),
    queryFn: () => getShiftAssignmentById(companyId!, id!),
    enabled: !!companyId && !!id,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

export function useRecentCustomShiftSlots(
  companyId: string | null,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: shiftsQueryKeys.recentCustomSlots(companyId),
    queryFn: () => getRecentCustomShiftSlots(companyId!),
    enabled: !!companyId && options?.enabled !== false,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

export function useCreateShiftAssignment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      companyId,
      payload,
    }: {
      companyId: string
      payload: CreateShiftAssignmentDto
    }) => createShiftAssignment(companyId, payload),
    onSuccess: async () => {
      await invalidateAllAssignmentViews(queryClient)
      await invalidateRecentCustomSlotViews(queryClient)
      toast.success('Assignment created')
    },
    onError: (error) => {
      // ASSIGNMENT_IN_THE_PAST is surfaced by the calling component as a
      // confirm dialog (backfill prompt); don't toast on top of it.
      const code = (error as { response?: { data?: { code?: string } } })?.response?.data?.code
      if (code === 'ASSIGNMENT_IN_THE_PAST') return
      toast.error(getShiftEligibilityErrorMessage(error) ?? getErrorMessage(error))
    },
  })
}

export function useBulkAssignShifts() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      companyId,
      payload,
    }: {
      companyId: string
      payload: BulkAssignShiftDto
      suppressSuccessToast?: boolean
      /** userId → display name, so skipped people are named in the result. */
      userNames?: Record<string, string>
    }) => bulkAssignShifts(companyId, payload),
    onSuccess: async (result, variables) => {
      await invalidateAllAssignmentViews(queryClient)
      await invalidateRecentCustomSlotViews(queryClient)
      if (!variables.suppressSuccessToast) {
        const summary = getBulkAssignToast(result, variables.userNames)
        toast[summary.variant](summary.message)
      }
    },
    onError: (error, variables) => {
      const result = extractBulkAssignErrorResult(error)
      if (result) {
        const summary = getBulkAssignToast(result, variables.userNames)
        toast[summary.variant](summary.message)
        return
      }
      toast.error(getErrorMessage(error))
    },
  })
}

/** Validates a ROTA workbook server-side. No cache changes — read-only preview. */
export function useRotaUploadPreview() {
  return useMutation({
    mutationFn: ({
      companyId,
      file,
      startDate,
      endDate,
      conflictPolicy,
    }: {
      companyId: string
      file: File
      startDate: string
      endDate: string
      conflictPolicy?: RotaConflictPolicy
    }) => previewRotaUpload(companyId, file, startDate, endDate, conflictPolicy),
  })
}

/** Commits a ROTA workbook (atomic replace) and refreshes the planning grid. */
export function useRotaUploadCommit() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      companyId,
      file,
      startDate,
      endDate,
      conflictPolicy,
    }: {
      companyId: string
      file: File
      startDate: string
      endDate: string
      conflictPolicy?: RotaConflictPolicy
    }) => commitRotaUpload(companyId, file, startDate, endDate, conflictPolicy),
    onSuccess: async (result) => {
      await invalidateAllAssignmentViews(queryClient)
      await invalidateRecentCustomSlotViews(queryClient)
      const ineligible = result.totals.ineligible ?? 0
      const message = `ROTA uploaded: ${result.totals.created} created, ${result.totals.replaced} replaced, ${result.totals.skipped ?? 0} skipped`
      if (ineligible > 0) {
        toast.warning(
          `${message}, ${ineligible} not created because the employee can't work that day (inactive account, employment ended or approved leave).`
        )
      } else {
        toast.success(message)
      }
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useUpdateShiftAssignment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      companyId,
      id,
      payload,
    }: {
      companyId: string
      id: string
      payload: UpdateShiftAssignmentDto
    }) => updateShiftAssignment(companyId, id, payload),
    onSuccess: async (_, variables) => {
      await invalidateAllAssignmentViews(queryClient)
      queryClient.invalidateQueries({
        queryKey: shiftAssignmentsQueryKeys.detail(variables.companyId, variables.id),
      })
      queryClient.invalidateQueries({ queryKey: attendanceKeys.myToday() })
      toast.success('Assignment updated')
    },
    onError: (error) => {
      toast.error(getShiftEligibilityErrorMessage(error) ?? getErrorMessage(error))
    },
  })
}

export function useConfirmShiftAssignment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ companyId, id }: { companyId: string; id: string }) =>
      confirmShiftAssignment(companyId, id),
    onSuccess: async (_, variables) => {
      await invalidateAllAssignmentViews(queryClient)
      queryClient.invalidateQueries({
        queryKey: shiftAssignmentsQueryKeys.detail(variables.companyId, variables.id),
      })
      queryClient.invalidateQueries({ queryKey: attendanceKeys.myToday() })
      queryClient.invalidateQueries({ queryKey: notificationsQueryKeys.all })
      toast.success('Assignment confirmed')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useAcknowledgeShiftAlert() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ companyId, alertId }: { companyId: string; alertId: string }) =>
      acknowledgeShiftAlert(companyId, alertId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationsQueryKeys.all })
      toast.success('Reminders stopped')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useCancelShiftAssignment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      companyId,
      id,
      payload,
    }: {
      companyId: string
      id: string
      payload: CancelShiftAssignmentDto
    }) => cancelShiftAssignment(companyId, id, payload),
    onSuccess: async (_, variables) => {
      await invalidateAllAssignmentViews(queryClient)
      queryClient.invalidateQueries({
        queryKey: shiftAssignmentsQueryKeys.detail(variables.companyId, variables.id),
      })
      queryClient.invalidateQueries({ queryKey: attendanceKeys.myToday() })
      queryClient.invalidateQueries({ queryKey: notificationsQueryKeys.all })
      toast.success('Assignment cancelled')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useDeleteShiftAssignment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ companyId, id }: { companyId: string; id: string }) =>
      deleteShiftAssignment(companyId, id),
    onSuccess: async () => {
      await invalidateAllAssignmentViews(queryClient)
      queryClient.invalidateQueries({ queryKey: attendanceKeys.myToday() })
      queryClient.invalidateQueries({ queryKey: notificationsQueryKeys.all })
      toast.success('Assignment deleted')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}
